import { ArrowRight, MessageCircleQuestion } from 'lucide-react';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/app/providers/AppContextProvider';
import ModalContact from '@/components/shared/ModalContact';
import Spinner from '@/components/shared/Spinner';
import { Button } from '@/components/ui/button';
import useAddOnQuotes from '@/hooks/useAddOnQuotes';
import { useFlowHighlightClass } from '@/hooks/useFlowGuidance';
import { isFeatureActive } from '@/lib/featureFlags';
import { cn } from '@/lib/utils';
import { SupportedProductType } from '@/types/api';
import { FeatureFlag } from '@/types/app';
import { buildCoverageSummary, buildProductEntries } from '../utils/productEntries';
import PackageTable from './PackageTable';

interface SummaryProps {
	id: string;
	isSaving: boolean;
	onBack: () => void;
	onConfirm: () => void;
}

const Summary = ({ id, isSaving, onBack, onConfirm }: SummaryProps): React.JSX.Element => {
	const { t } = useTranslation();
	const { applicationData, appData, config } = useAppContext();
	// Flow guidance points here on the overview step: highlight the confirm button.
	const confirmHighlight = useFlowHighlightClass('confirm-review');

	const [advisorModalOpen, setAdvisorModalOpen] = useState(false);
	const showAdvisorContactEntry = isFeatureActive(
		config,
		FeatureFlag.ENABLE_SHARE_WITH_ADVISOR,
		true
	);
	// When a label routes clients exclusively through their advisor, the Apply
	// button is hidden and only the Talk-to-advisor flow remains. The backend
	// also rejects direct submissions for these labels (allow_direct_application).
	const allowDirectApplication = isFeatureActive(
		config,
		FeatureFlag.ALLOW_DIRECT_APPLICATION,
		true
	);

	const productTypes = useMemo<SupportedProductType[]>(() => {
		const raw = appData?.relevant_product_types ?? [];
		const allowed = new Set<string>(Object.values(SupportedProductType));
		return raw.filter((pt): pt is SupportedProductType => allowed.has(pt));
	}, [appData?.relevant_product_types]);

	const {
		occupationalHealthServiceQuotes,
		wgaExtendedQuotes,
		wgaQuotes,
		wiaExcessQuotes,
		isLoading: isFetchingQuotes,
		error: fetchQuotesError,
		refetch: fetchQuotes
	} = useAddOnQuotes(id, productTypes);

	const entries = useMemo(
		() =>
			buildProductEntries({
				relevantProductTypes: productTypes,
				applicationData,
				appData,
				occupationalHealthServiceQuotes,
				wgaExtendedQuotes,
				wgaQuotes,
				wiaExcessQuotes,
				t
			}),
		[
			productTypes,
			appData,
			applicationData,
			occupationalHealthServiceQuotes,
			wgaExtendedQuotes,
			wgaQuotes,
			wiaExcessQuotes,
			t
		] // prettier-ignore
	);

	const selectedEntries = entries.filter((e) => e.selectedQuote !== null);
	const totalPremiumEur = selectedEntries.reduce((sum, e) => sum + (e.premiumEur ?? 0), 0);
	const hasUnresolvedSelectedAddOn =
		(!!applicationData.selected_occupational_health_service_quote_id &&
			!entries.some(
				(entry) =>
					entry.key === SupportedProductType.OCCUPATIONAL_HEALTH_SERVICE && entry.selectedQuote
			)) ||
		(!!applicationData.selected_wga_gap_extended_quote_id &&
			!entries.some(
				(entry) =>
					entry.key === SupportedProductType.SICK_LEAVE_LONG_TERM_WGA_GAP_EXTENDED &&
					entry.selectedQuote
			)) ||
		(!!applicationData.selected_wga_gap_quote_id &&
			!entries.some(
				(entry) =>
					entry.key === SupportedProductType.SICK_LEAVE_LONG_TERM_WGA_GAP && entry.selectedQuote
			)) ||
		(!!applicationData.selected_wia_excess_quote_id &&
			!entries.some(
				(entry) =>
					entry.key === SupportedProductType.SICK_LEAVE_LONG_TERM_WIA_EXCESS && entry.selectedQuote
			));

	const sickLeaveCoverage = appData?.premium_variables?.coverage?.sick_leave_short_term;
	const coverageSummary = buildCoverageSummary(sickLeaveCoverage, t);

	const currentPolicy = appData?.current_policy?.sick_leave_short_term as
		| { insurer_name?: string; premium_rate_percentage?: number }
		| undefined;

	if (isFetchingQuotes || !appData) {
		return (
			<div className="select-none flex justify-center py-20">
				<Spinner size="lg" visible />
			</div>
		);
	}

	if (fetchQuotesError || hasUnresolvedSelectedAddOn) {
		return (
			<div className="select-none">
				<h1 className="text-2xl md:text-3xl font-bold mb-4 md:mb-8 leading-tight">
					{t('productPackage.overview.title')}
				</h1>
				<div className="border border-destructive/30 rounded-lg bg-destructive/5 px-5 py-4 mb-8">
					<p className="text-destructive mb-4">{t('productPackage.overview.fetchError')}</p>
					<Button type="button" variant="outline" onClick={fetchQuotes} disabled={isSaving}>
						{t('common.retry')}
					</Button>
				</div>
				<div className="flex flex-col-reverse sm:flex-row sm:justify-center gap-2 sm:gap-3 py-2 lg:py-6">
					<Button
						type="button"
						variant="outline"
						size="xl"
						className="font-bold w-full sm:w-auto"
						onClick={onBack}
						disabled={isSaving}
						data-testid="application-previous-button"
					>
						{t('application.navigation.previous')}
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="select-none">
			<h1 className="text-2xl md:text-3xl font-bold mb-2 md:mb-8 leading-tight">
				{t('productPackage.overview.title')}
			</h1>

			{/* Selected products */}
			<section className="mb-8">
				<h2 className="text-xl font-semibold text-primary mb-4 md:mb-5">
					{t('productPackage.overview.selectedProducts')}
				</h2>
				<PackageTable
					entries={selectedEntries}
					totalPremiumEur={totalPremiumEur}
					coverageSummary={coverageSummary}
				/>
			</section>

			{/* Current policy comparison */}
			{(currentPolicy?.insurer_name || currentPolicy?.premium_rate_percentage) && (
				<section className="mb-8">
					<h2 className="text-lg font-semibold text-primary mb-4">
						{t('productPackage.overview.currentPolicy')}
					</h2>
					<div className="border border-border rounded-lg overflow-hidden">
						{currentPolicy.insurer_name && (
							<div className="flex items-center justify-between px-5 py-4 border-b border-border">
								<span className="text-sm text-muted-foreground">
									{t('productPackage.overview.currentInsurer')}
								</span>
								<span className="text-sm font-semibold text-foreground">
									{currentPolicy.insurer_name}
								</span>
							</div>
						)}
						{currentPolicy.premium_rate_percentage && (
							<div className="flex items-center justify-between px-5 py-4">
								<span className="text-sm text-muted-foreground">
									{t('productPackage.overview.currentPremiumRate')}
								</span>
								<span className="text-sm font-semibold text-foreground">
									{currentPolicy.premium_rate_percentage}%
								</span>
							</div>
						)}
					</div>
				</section>
			)}

			{/* Contract description */}
			{allowDirectApplication && (
				<>
					<p className="text-base md:text-lg mb-4 leading-relaxed text-foreground/80">
						{t('application.steps.step1.contractDescription')}
					</p>
					<p className="text-base md:text-lg mb-8 leading-relaxed text-foreground/80">
						{t('application.steps.step1.billingDescription1')}{' '}
						{t('application.steps.step1.billingDescription2')}
					</p>
				</>
			)}

			{/* Navigation */}
			{showAdvisorContactEntry ? (
				<>
					<div className="flex flex-col-reverse sm:flex-row sm:justify-center gap-2 sm:gap-3 py-2 lg:py-6">
						<Button
							type="button"
							variant="outline"
							size="xl"
							className="font-bold w-full sm:w-auto"
							onClick={() => setAdvisorModalOpen(true)}
							disabled={isSaving}
							iconRight={<MessageCircleQuestion className="w-4 h-4" />}
							data-testid="overview-discuss-with-advisor-button"
						>
							{t('application.navigation.discussWithAdvisor')}
						</Button>
						{allowDirectApplication && (
							<Button
								type="button"
								variant="default"
								size="xl"
								className={cn('font-bold w-full sm:w-auto', confirmHighlight)}
								onClick={onConfirm}
								disabled={isSaving}
								loading={isSaving}
								data-testid="application-next-button"
								iconRight={<ArrowRight className="w-4 h-4" />}
							>
								{t('productPackage.overview.submitButton')}
							</Button>
						)}
					</div>
					<div className="text-center pb-2 lg:pb-6">
						<Button
							type="button"
							variant="ghost"
							size="xl"
							className="text-muted-foreground hover:text-foreground"
							onClick={onBack}
							disabled={isSaving}
							data-testid="application-previous-button"
						>
							{t('application.navigation.goBack')}
						</Button>
					</div>
					<ModalContact
						open={advisorModalOpen}
						onOpenChange={setAdvisorModalOpen}
						marketingTitle={t('contact.modal.advisorTitle')}
						marketingDescription={t('contact.modal.advisorDescription')}
					/>
				</>
			) : (
				<div className="flex flex-col-reverse sm:flex-row sm:justify-center gap-2 sm:gap-3 py-2 lg:py-6">
					<Button
						type="button"
						variant="outline"
						size="xl"
						className="font-bold w-full sm:w-auto"
						onClick={onBack}
						disabled={isSaving}
						data-testid="application-previous-button"
					>
						{t('application.navigation.previous')}
					</Button>
					{allowDirectApplication && (
						<Button
							type="button"
							variant="default"
							size="xl"
							className={cn('font-bold w-full sm:w-auto', confirmHighlight)}
							onClick={onConfirm}
							disabled={isSaving}
							loading={isSaving}
							data-testid="application-next-button"
						>
							{t('productPackage.overview.submitButton')}
						</Button>
					)}
				</div>
			)}
		</div>
	);
};

export default Summary;
